import { useEffect, useRef } from 'react';
import { useMap } from 'react-leaflet';
import L from 'leaflet';
import type { SettlementCentroid } from '@/lib/map/eastern-galilee';

const MIN_LABEL_ZOOM = 11;

export default function SettlementLabelLayer({
  settlements,
}: {
  settlements: SettlementCentroid[];
}) {
  const map = useMap();
  const layerRef = useRef<L.LayerGroup | null>(null);

  useEffect(() => {
    const group = L.layerGroup();
    for (const s of settlements) {
      if (!Number.isFinite(s.lat) || !Number.isFinite(s.lng)) continue;
      const marker = L.marker([s.lat, s.lng], {
        interactive: false,
        icon: L.divIcon({
          className: 'eshkol-settlement-label',
          html: `<span dir="rtl">${escapeHtml(s.name)}</span>`,
          iconSize: [0, 0],
          iconAnchor: [0, 0],
        }),
      });
      group.addLayer(marker);
    }
    group.addTo(map);
    layerRef.current = group;

    const syncVisibility = () => {
      const visible = map.getZoom() >= MIN_LABEL_ZOOM;
      if (visible && !map.hasLayer(group)) group.addTo(map);
      if (!visible && map.hasLayer(group)) map.removeLayer(group);
    };
    syncVisibility();
    map.on('zoomend', syncVisibility);

    return () => {
      map.off('zoomend', syncVisibility);
      if (layerRef.current) {
        map.removeLayer(layerRef.current);
        layerRef.current = null;
      }
    };
  }, [map, settlements]);

  return null;
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}
